'use client';
import { redirect, usePathname, useSearchParams } from 'next/navigation';
import {
  HomePath,
  LoginPath,
  SignUpPath,
  InvitePath,
  ErrorPath,
  VerifyPath,
  SlackAdminApprovedPath
} from '../lib/paths';
import type { ReactNode } from 'react';
import { useContext, useEffect, useState, useCallback } from 'react';
import LoadingLayoutOverlay from './LoadingLayoutOverlay';
import { MeContext } from '../providers/Me';

import useInterval from '../hooks/use_interval';
import config from '../lib/config';

/**
 * Determines if there is a wildcard prefix in domain
 * Example: test 'abc.mcp.natoma.app' would match 'mcp.natoma', but not 'natoma.app'
 * @param str the input string to test
 * @param subdomain tail of subdomain that we want to match e.g. mcp.natoma.app
 * @returns true if it contains only 1 wildcard prefix
 */
export function matchesWildcardSubdomain(str: string, subdomain: string) {
  const escapedDomain = subdomain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`^[^.]+\\.${escapedDomain}\\b`);
  return regex.test(str);
}

const useIsLoginOrSignUpPage = (): boolean => {
  const path = usePathname();
  const isLoginOrSignUpPage =
    path === LoginPath ||
    path === VerifyPath ||
    path === SignUpPath ||
    path === InvitePath ||
    path === ErrorPath ||
    path === SlackAdminApprovedPath;
  return isLoginOrSignUpPage;
};

/**
 * Handles redirecting the page if a user is logged-in or not.
 *
 * @param props
 * @returns
 */
export default function AuthGuard({
  children,
  layout
}: {
  children: ReactNode;
  layout: 'home' | 'tenant';
}) {
  const path = usePathname();
  const searchParams = useSearchParams();
  const [childContents, setChildContents] = useState<ReactNode>(null);

  const isLoginOrSignUpPage = useIsLoginOrSignUpPage();

  const { loading, isAuthenticated, isAuthorized, refreshSession } =
    useContext(MeContext);

  const allowed = layout === 'tenant' ? isAuthorized : isAuthenticated;

  const redirectPath = searchParams.get('redirect');

  useEffect(() => {
    if (loading) {
      setChildContents(<LoadingLayoutOverlay />);
    } else if (isLoginOrSignUpPage) {
      // already logged in, redirect to the page the user originally wanted to access
      if (allowed) {
        if (redirectPath) {
          redirect(redirectPath);
        } else {
          redirect(HomePath);
        }
      } else {
        setChildContents(<>{children}</>);
      }
    } else {
      // not logged in, redirect to login page
      if (allowed) {
        setChildContents(<>{children}</>);
      } else {
        redirect(
          LoginPath +
            (path !== '/'
              ? `?redirect=${encodeURIComponent(window.location.href)}`
              : '')
        );
      }
    }
  }, [loading, isLoginOrSignUpPage, allowed, redirectPath, children, path]);

  const pollAuthStatus = useCallback(() => {
    if (!isLoginOrSignUpPage) {
      refreshSession({ silent: true });
    }
  }, [isLoginOrSignUpPage, refreshSession]);

  useInterval(pollAuthStatus, config.auth.pollSessionTime);

  return childContents;
}
